import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { SEO } from "@/components/SEO";
import { useStaticTranslation } from "@/lib/staticTranslations";

const Accessibility = () => {
  const { t, isHebrew } = useStaticTranslation("accessibility");
  const { t: seoT } = useStaticTranslation("seo");

  return (
    <PageTransition>
      <SEO
        title={seoT("accessibilityTitle", "הצהרת נגישות")}
        description={seoT("accessibilityDescription", "הצהרת הנגישות של אליס תכשיטים - מידע על התאמות נגישות באתר, תמיכה בטכנולוגיות מסייעות ודרכי פניה לנושא נגישות")}
        canonical="https://alice.jewelry/accessibility"
        breadcrumbs={[
          { name: seoT("home", "דף הבית"), url: '/' },
          { name: seoT("breadcrumbAccessibility", "הצהרת נגישות"), url: '/accessibility' },
        ]}
      />
      <div className="min-h-screen flex flex-col" dir={isHebrew ? "rtl" : "ltr"}>
        <Header />
        
        <main className="flex-1">
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-4xl">
              <div className="text-center mb-12 animate-fade-in">
                <div className="gold-divider mb-6" />
                <h1 className="text-4xl md:text-5xl font-serif mb-4">{t("title", "הצהרת נגישות")}</h1>
                <p className="text-lg text-muted-foreground">{t("companyName", "תכשיטי אליס בע\"מ")}</p>
                <p className="text-sm text-muted-foreground mt-2">{t("updatedDate", "הצהרת הנגישות עודכנה בתאריך 10.10.2023")}</p>
              </div>

              <div className={`space-y-6 ${isHebrew ? 'text-right' : 'text-left'} animate-fade-in`} style={{ animationDelay: '0.1s' }}>
                <p className="text-muted-foreground leading-relaxed">
                  {t("intro", "אנו בתכשיטי אליס, שואפים להבטיח כי השירותים הדיגיטליים יהיו נגישים לאנשים עם מוגבלויות, ולפיכך השקענו משאבים על מנת להקל את השימוש באתר עבור אנשים עם מוגבלויות, ככל האפשר, מתוך אמונה כי לכל אדם מגיעה הזכות לחיות בשוויון, כבוד, נוחות ועצמאות.")}
                </p>

                <p className="text-muted-foreground leading-relaxed">
                  {t("importance", "אנו מייחסים חשיבות רבה למתן אפשרות שווה לאנשים עם מוגבלות להשתמש במידע המוצג באתר:")}
                </p>

                <div>
                  <h2 className="text-2xl font-serif mb-3">{t("accessibleSiteTitle", "מהות אתר אינטרנט נגיש")}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("accessibleSiteDesc", "אתר אינטרנט נגיש, הוא אתר המאפשר לאדם עם מוגבלות, לגלוש באותה רמת יעילות והנאה כגולשים אחרים, תוך שימוש ביכולות המערכת עליה הוא פועל ובאמצעות טכנולוגיות מסייעות לנגישות.")}
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-serif mb-3">{t("adaptationsTitle", "ביצוע התאמות הנגישות באתר האינטרנט")}</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("adaptationsDesc", "התאמות הנגישות באתר בוצעו בהתאם לסימן ג': שירותי האינטרנט בתקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות) התשע\"ג 2013, לתקן הישראלי ת\"י 5568 המבוסס על הנחיות WCAG 2.0, האתר הונגש לרמה AA ובכפוף לשינויים והתאמות שבוצעו במסמך התקן הישראלי.")}
                  </p>
                </div>

                <div className="bg-muted/30 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-3">{t("supportTitle", "האתר תומך בשימוש בטכנולוגיות מסייעות כגון:")}</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• {t("support1", "תוכנות הקראת מסך")}</li>
                    <li>• {t("support2", "גלישה בעזרת מקלדת")}</li>
                    <li>• {t("support3", "הגדלת פונט האתר ל־4 גדלים שונים")}</li>
                    <li>• {t("support4", "חסימת הבהובים")}</li>
                    <li>• {t("support5", "התאמת ניגודיות")}</li>
                    <li>• {t("support6", "התאמת אתר לעיוורי צבעים")}</li>
                    <li>• {t("support7", "שינוי הפונט לקריא יותר")}</li>
                    <li>• {t("support8", "הגדלת הסמן")}</li>
                    <li>• {t("support9", "הגדלת התצוגה לכ־200%")}</li>
                    <li>• {t("support10", "הדגשת קישורים וכותרות")}</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">{t("orderAccessibility", "נגישות עבור הזמנות באתר")}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("orderAccessibilityDesc", "ככל שהנך נדרש להתאמות ו/או הסדרי נגישות בעת ביצוע הרכישה ו/או לצורך תיאום וקבלת המשלוח אנא ציין זאת בהערות ההזמנה.")}
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-serif mb-3">{t("contactTitle", "דרכי פניה")}</h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    {t("contactIntro", "במידה ומצאתם באתר האינטרנט בעיה בנושא הנגישות או שהנכם זקוקים עזרה, אתם מוזמנים לפנות אלינו:")}
                  </p>
                  <div className="space-y-1 text-muted-foreground">
                    <p>{t("contactPhone", "טלפון: 079-5575576")}</p>
                    <p>{t("contactEmail", "דוא\"ל: store@alice.jewelry")}</p>
                  </div>
                </div>

                <p className="text-center text-muted-foreground italic mt-8">
                  {t("closingMessage", "מאחלים לכם חווית גלישה נעימה באתרנו!")}
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default Accessibility;
